// src/components/BreadcrumbsSchema.tsx
import { useLocation, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useMemo } from 'react';

const SITE_URL =
  import.meta.env.VITE_SITE_URL?.replace(/\/$/, '') ||
  (globalThis?.location?.origin ?? 'https://example.com');

function titleFromSlug(slug: string) {
  const decoded = decodeURIComponent(slug);
  const cleaned = decoded.replaceAll('-', ' ').replaceAll('_', ' ').split(/\s+/).join(' ').trim();
  return cleaned
    .split(' ')
    .map((w: string) => (w ? w[0].toUpperCase() + w.slice(1) : ''))
    .join(' ');
}

export default function BreadcrumbsSchema() {
  const { pathname } = useLocation();
  const { packageId } = useParams();

  const items = useMemo(() => {
    const segments = pathname.split('/').filter(Boolean);
    const crumbs: { name: string; item: string }[] = [{ name: 'Home', item: `${SITE_URL}/` }];
    if (segments.length === 0) return crumbs;

    let acc = '';
    for (const [idx, seg] of segments.entries()) {
      acc += `/${seg}`;
      let name: string;

      if (seg === 'packages') name = 'Packages';
      else if (seg === 'gallery') name = 'Gallery';
      else if (seg === 'about') name = 'About';
      else if (seg === 'contact') name = 'Contact';
      else if (segments[0] === 'packages' && idx === 1 && packageId) {
        name = titleFromSlug(packageId); // prettify the package id/slug
      } else {
        name = titleFromSlug(seg);
      }

      crumbs.push({ name, item: `${SITE_URL}${acc}` });
    }

    return crumbs;
  }, [pathname, packageId]);

  const jsonLd = {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.name,
      item: c.item,
    })),
  };

  return (
    <Helmet>
      <script
        type="application/ld+json"
        suppressHydrationWarning
        dangerouslySetInnerHTML={{ __html: JSON.stringify({ '@context': 'https://schema.org', ...jsonLd }) }}
      />
    </Helmet>
  );
}
